/**
 * VoiceCloneTab Component
 *
 * Main container for voice cloning feature.
 * Requires authentication to create and manage voice clones.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { LoginModal } from '@/components/auth';
import { useVoices, useVoiceUpload } from '../../hooks/useVoices';
import { voiceApi } from '../../services/voiceApi';
import VoiceRecorder from './VoiceRecorder';
import VoiceUploader from './VoiceUploader';
import VoiceList from './VoiceList';
import { NLLBLanguageSelector } from '../common/LanguageSelector';

type InputMode = 'record' | 'upload';

export const VoiceCloneTab: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoginModalOpen, loginMessage, closeLoginModal, requireAuth } =
    useRequireAuth();
  const [inputMode, setInputMode] = useState<InputMode>('upload');
  const [voiceName, setVoiceName] = useState('');
  const [languageHint, setLanguageHint] = useState('');
  const [selectedAudio, setSelectedAudio] = useState<Blob | File | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const releaseAudioRef = useRef<(() => void) | null>(null);
  // Bumped on every stop/replace so a load that is still in flight knows the
  // user moved on, and drops its bytes instead of playing over the new voice.
  const playRequestRef = useRef(0);

  const { voices, loading, error, fetchVoices, deleteVoice } = useVoices();
  const { uploading, progress, error: uploadError, uploadVoice } = useVoiceUpload();

  /**
   * Handle audio recorded from microphone.
   */
  const handleRecordingComplete = useCallback((audioBlob: Blob) => {
    setSelectedAudio(audioBlob);
    setInputError(null);
  }, []);

  /**
   * Handle file selected from uploader.
   */
  const handleFileSelected = useCallback((file: File) => {
    setSelectedAudio(file);
    setInputError(null);
  }, []);

  /**
   * Handle the child discarding its audio ("Record Again", the uploader's X, or
   * a newly picked file that failed validation). This component owns the
   * selection, so dropping it here is what actually prevents the stale blob
   * from being uploaded.
   */
  const handleAudioDiscarded = useCallback(() => {
    setSelectedAudio(null);
  }, []);

  /**
   * Switch between recording and uploading.
   * The outgoing child unmounts and loses its state, so the selection it gave
   * us is no longer represented anywhere on screen - drop it instead of keeping
   * an invisible blob armed behind the Create Voice button.
   */
  const handleInputModeChange = useCallback((mode: InputMode) => {
    setInputMode(mode);
    setSelectedAudio(null);
    setInputError(null);
  }, []);

  /**
   * Handle form submission.
   * Requires authentication before uploading.
   */
  const handleSubmit = useCallback(() => {
    if (!selectedAudio || !voiceName.trim()) {
      return;
    }

    // Require authentication before uploading
    requireAuth(async () => {
      try {
        // Convert Blob to File if needed
        // Recorded audio from MediaRecorder is webm format, preserve the correct type
        const file =
          selectedAudio instanceof File
            ? selectedAudio
            : new File([selectedAudio], 'recorded-voice.webm', {
                type: selectedAudio.type || 'audio/webm',
              });

        await uploadVoice(file, {
          name: voiceName.trim(),
          language_hint: languageHint || undefined,
        });

        // Reset form
        setVoiceName('');
        setLanguageHint('');
        setSelectedAudio(null);
        setInputError(null);

        // Refresh voice list
        await fetchVoices(true);
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }, t('voiceClone.loginToCreate'));
  }, [selectedAudio, voiceName, languageHint, uploadVoice, fetchVoices, requireAuth, t]);

  /**
   * Handle voice deletion.
   */
  const handleDelete = useCallback(
    async (voiceId: string) => {
      await deleteVoice(voiceId);
    },
    [deleteVoice]
  );

  /**
   * Tear down whatever is playing or loading right now.
   *
   * Releases the fetched audio as well as stopping it: the bytes are held by an
   * object URL that nothing else will ever free.
   */
  const teardownPlayback = useCallback(() => {
    playRequestRef.current += 1;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (releaseAudioRef.current) {
      releaseAudioRef.current();
      releaseAudioRef.current = null;
    }
  }, []);

  // Leaving the tab mid-playback must not strand the audio in memory.
  useEffect(() => teardownPlayback, [teardownPlayback]);

  /**
   * Handle voice play.
   *
   * The audio is fetched and played from a blob rather than pointed at
   * directly: our own endpoint answers with Cross-Origin-Resource-Policy:
   * same-origin, so a media element aimed at it has its response discarded by
   * the browser on the desktop build (file:// page, http://localhost backend).
   * voiceApi.loadVoiceAudio carries the full reasoning.
   *
   * Every failure here used to be silent: no audio_url did nothing at all, a
   * load error only reset the icon, and play() rejections were unhandled. The
   * user pressed play and the app appeared to ignore them, with no way to tell
   * a broken URL from a missing file. Each path now says what happened.
   */
  const handlePlay = useCallback(
    (voiceId: string) => {
      teardownPlayback();
      setInputError(null);

      const voice = voices.find((v) => v.voice_id === voiceId);
      if (!voice?.audio_url) {
        setInputError(t('voiceClone.playbackUnavailable'));
        return;
      }

      const requestId = playRequestRef.current;
      setPlayingVoiceId(voiceId);

      const fail = () => {
        teardownPlayback();
        setPlayingVoiceId(null);
        setInputError(t('voiceClone.playbackFailed'));
      };

      void voiceApi
        .loadVoiceAudio(voice)
        .then((playable) => {
          // The user pressed stop, or another voice, while the bytes loaded.
          if (requestId !== playRequestRef.current) {
            playable.release();
            return;
          }

          const audio = new Audio(playable.src);
          audioRef.current = audio;
          releaseAudioRef.current = playable.release;

          audio.onended = () => {
            teardownPlayback();
            setPlayingVoiceId(null);
          };

          audio.onerror = fail;

          // play() rejects on autoplay policy and on a failed load; without this
          // the rejection is swallowed and the button just looks stuck.
          return audio.play().catch(fail);
        })
        .catch(() => {
          if (requestId !== playRequestRef.current) {
            return;
          }
          fail();
        });
    },
    [voices, t, teardownPlayback]
  );

  /**
   * Handle voice stop.
   */
  const handleStop = useCallback(() => {
    teardownPlayback();
    setPlayingVoiceId(null);
  }, [teardownPlayback]);

  return (
    <div className="voice-clone-tab px-3 sm:px-6 py-4 sm:py-6 max-w-4xl mx-auto">
      <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-secondary-900 dark:text-secondary-100">
        {t('voiceClone.title')}
      </h1>

      {/* Supported languages info */}
      <div className="mb-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-2 sm:gap-3">
          <svg
            className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-medium text-blue-700 dark:text-blue-300 break-words">
              {t('voiceClone.supportedLanguages')}: {t('voiceClone.supportedLanguagesList')}
            </p>
            <p className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 mt-1">
              {t('voiceClone.supportedLanguagesInfo')}
            </p>
          </div>
        </div>
      </div>

      {/* Authentication warning */}
      {!isAuthenticated && (
        <div className="mb-4 p-3 sm:p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-2 sm:gap-3">
            <svg
              className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="min-w-0">
              <p className="text-sm sm:text-base font-medium text-amber-700 dark:text-amber-300">
                {t('voiceClone.loginRequired')}
              </p>
              <p className="text-xs sm:text-sm text-amber-600 dark:text-amber-400 mt-1">
                {t('voiceClone.loginRequiredDescription')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error display - input errors first, they describe the latest action */}
      {(inputError ?? uploadError ?? error) && (
        <div className="mb-4 p-3 sm:p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm sm:text-base">
          {inputError ?? uploadError ?? error}
        </div>
      )}

      {/* Create new voice section */}
      <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700">
        <h2 className="text-lg sm:text-xl font-semibold mb-4 text-secondary-900 dark:text-secondary-100">
          {t('voiceClone.createNew')}
        </h2>

        {/* Input mode tabs */}
        <div className="flex mb-4 border-b border-secondary-200 dark:border-secondary-700 overflow-x-auto">
          <button
            onClick={() => handleInputModeChange('upload')}
            className={`px-3 sm:px-4 py-2 -mb-px border-b-2 transition-colors text-sm sm:text-base whitespace-nowrap min-h-[44px] ${
              inputMode === 'upload'
                ? 'border-primary-500 text-primary-500'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-300'
            }`}
          >
            {t('voiceClone.uploadTab')}
          </button>
          <button
            onClick={() => handleInputModeChange('record')}
            className={`px-3 sm:px-4 py-2 -mb-px border-b-2 transition-colors text-sm sm:text-base whitespace-nowrap min-h-[44px] ${
              inputMode === 'record'
                ? 'border-primary-500 text-primary-500'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-300'
            }`}
          >
            {t('voiceClone.recordTab')}
          </button>
        </div>

        {/* Input component */}
        <div className="mb-4">
          {inputMode === 'upload' ? (
            <VoiceUploader
              onFileSelected={handleFileSelected}
              onFileCleared={handleAudioDiscarded}
              onError={setInputError}
              disabled={uploading}
            />
          ) : (
            <VoiceRecorder
              onRecordingComplete={handleRecordingComplete}
              onRecordingDiscarded={handleAudioDiscarded}
              onError={setInputError}
              disabled={uploading}
            />
          )}
        </div>

        {/* Voice name input */}
        <div className="mb-4">
          <label className="label-themed-sm">{t('voiceClone.voiceName')} *</label>
          <input
            type="text"
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            placeholder={t('voiceClone.voiceNamePlaceholder')}
            className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 min-h-[44px]"
            maxLength={100}
            disabled={uploading}
          />
        </div>

        {/* Language hint input (optional) */}
        <div className="mb-4">
          <label className="label-themed-sm">
            {t('voiceClone.languageHint')} ({t('common.optional')})
          </label>
          <NLLBLanguageSelector
            values={languageHint ? [languageHint] : []}
            onMultiChange={(languages) => {
              // Convert 'auto' to empty string for auto-detect mode
              const selected = languages[0] || '';
              setLanguageHint(selected === 'auto' ? '' : selected);
            }}
            disabled={uploading}
            maxSelection={1}
            showQuickActions={false}
            showAutoDetect={true}
            placeholder={t('voiceClone.autoDetect')}
          />
        </div>

        {/* Upload progress */}
        {uploading && (
          <div className="mb-4">
            <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-secondary-500 mt-1">
              {t('voiceClone.uploading')} {progress}%
            </p>
          </div>
        )}

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={!selectedAudio || !voiceName.trim() || uploading}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] text-sm sm:text-base transition-colors"
        >
          {uploading ? t('voiceClone.creating') : t('voiceClone.createVoice')}
        </button>
      </div>

      {/* My voices section */}
      <div className="p-4 sm:p-6 bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700">
        <div className="flex items-center justify-between mb-4 gap-2">
          <h2 className="text-lg sm:text-xl font-semibold text-secondary-900 dark:text-secondary-100">
            {t('voiceClone.myVoices')}
          </h2>
          <button
            onClick={() => fetchVoices(true)}
            className="text-primary-500 hover:text-primary-600 text-sm sm:text-base min-h-[44px] px-2"
            disabled={loading}
          >
            {t('common.refresh')}
          </button>
        </div>

        <VoiceList
          voices={voices}
          loading={loading}
          onDelete={handleDelete}
          onPlay={handlePlay}
          onStop={handleStop}
          playingVoiceId={playingVoiceId}
        />
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />
    </div>
  );
};

export default VoiceCloneTab;
